// mediaManagment/hlsResolutionProfiles
//
// Profils de transcodage HLS proposés à la création d'une vidéo. Les plafonds de débit doivent
// rester synchronisés avec ceux réellement utilisés par le worker de transcodage
// (video-transcoding-worker/app/ffmpeg_builder.py, RESOLUTION_SPECS et COMPRESSION_CRF) : c'est
// sur ces valeurs que se base l'estimation de taille finale présentée à l'admin avant upload.
//
// Depuis le réglage de compression, le débit n'est plus imposé : le worker encode en qualité
// constante (CRF) et le débit suit le contenu, plafonné par `maxrate`. La taille ci-dessous est
// donc une ESTIMATION : un plan fixe ressort bien en dessous, une scène très chargée touche le
// plafond.

export type HlsResolutionId = '2160p' | '1080p' | '720p' | '480p';

/** 0 = sans compression, 1 = légère, 2 = bon compromis, 3 = forte compression. */
export type CompressionLevel = 0 | 1 | 2 | 3;

/** Niveau proposé d'office (CRF 23, le comportement qui précédait le réglage). */
export const DEFAULT_COMPRESSION_LEVEL: CompressionLevel = 2;

export interface HlsResolutionProfile {
  id               : HlsResolutionId;
  label            : string;
  maxrateKbps      : number;   // plafond de débit vidéo (-maxrate)
  audioBitrateKbps : number;
}

export const HLS_RESOLUTION_PROFILES: HlsResolutionProfile[] = [
  { id: '2160p', label: '2160p (4K)',      maxrateKbps: 25000, audioBitrateKbps: 192 },
  { id: '1080p', label: '1080p (Full HD)', maxrateKbps: 12000, audioBitrateKbps: 192 },
  { id: '720p',  label: '720p (HD)',       maxrateKbps: 5000,  audioBitrateKbps: 128 },
  { id: '480p',  label: '480p (SD)',       maxrateKbps: 2000,  audioBitrateKbps: 96  },
];

/**
 * Même règle que FfmpegCommandBuilder.is_4k_source côté worker : grand côté >= 3840 ou petit
 * côté >= 2160. Symétrique, donc valable pour une vidéo portrait.
 */
export const isSource4K = (width: number, height: number): boolean =>
  Math.max(width, height) >= 3840 || Math.min(width, height) >= 2160;

/** Le 2160p n'est proposé que sur une source en 4K : au-dessous, il ne ferait qu'agrandir. */
export const selectableHlsProfiles = (source4K: boolean): HlsResolutionProfile[] =>
  HLS_RESOLUTION_PROFILES.filter(profile => source4K || profile.id !== '2160p');

export interface CompressionLevelInfo {
  level          : CompressionLevel;
  crf            : number;   // valeur appliquée par le worker (informatif, non envoyé)
  shortLabel     : string;   // sous la graduation
  title          : string;
  quality        : string;
  storage        : string;
  qualityPips    : number;   // 1-4, jauge de qualité
  storagePips    : number;   // 1-4, jauge de place occupée
  /**
   * Débit vidéo moyen estimé, en part du plafond de la résolution. Niveaux 1-3 : ordre de
   * grandeur mesuré (chaque tranche de 6 CRF double à peu près la taille ; une source simple
   * ressort plus bas, une source chargée touche le plafond). Niveau 0 (CRF 12, quasi sans perte) :
   * il demande plus que le plafond sur la plupart des contenus, donc on compte le plafond.
   */
  bitrateShare   : number;
}

export const COMPRESSION_LEVELS: CompressionLevelInfo[] = [
  { level: 0, crf: 12, shortLabel: 'Aucune',    title: 'Sans compression',  quality: 'excellente', storage: 'volumineux', qualityPips: 4, storagePips: 4, bitrateShare: 1   },
  { level: 1, crf: 18, shortLabel: 'Légère',    title: 'Compression légère', quality: 'bonne',      storage: 'normal',     qualityPips: 3, storagePips: 3, bitrateShare: 0.7 },
  { level: 2, crf: 23, shortLabel: 'Compromis', title: 'Bon compromis',      quality: 'moyenne',    storage: 'optimisé',   qualityPips: 2, storagePips: 2, bitrateShare: 0.4 },
  { level: 3, crf: 28, shortLabel: 'Forte',     title: 'Forte compression',  quality: 'passable',   storage: 'réduit',     qualityPips: 1, storagePips: 1, bitrateShare: 0.2 },
];

export const getCompressionLevelInfo = (level: CompressionLevel): CompressionLevelInfo =>
  COMPRESSION_LEVELS[level] ?? COMPRESSION_LEVELS[DEFAULT_COMPRESSION_LEVEL];

/**
 * Taille finale estimée (en octets) pour ce profil et ce niveau de compression, à partir de la
 * durée de la vidéo source.
 */
export const estimateHlsSizeBytes = (
  profile         : HlsResolutionProfile,
  durationSeconds : number,
  level           : CompressionLevel = DEFAULT_COMPRESSION_LEVEL
): number => {
  const videoKbps      = profile.maxrateKbps * getCompressionLevelInfo(level).bitrateShare;
  const bytesPerSecond = ((videoKbps + profile.audioBitrateKbps) * 1000) / 8;
  return bytesPerSecond * Math.max(0, durationSeconds);
};

/** Borne haute (niveaux 1-3) : le débit colle au plafond sur toute la durée. */
export const maxHlsSizeBytes = (profile: HlsResolutionProfile, durationSeconds: number): number =>
  ((profile.maxrateKbps + profile.audioBitrateKbps) * 1000 / 8) * Math.max(0, durationSeconds);

export const formatEstimatedSize = (bytes: number): string => {
  if (bytes <= 0)                  return '—';
  if (bytes < 1024 * 1024)         return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024)  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};
